import React, { useState, useEffect } from 'react';
import { apiFetch, getApiBaseUrl } from '../../../utils/apiConfig';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Alert,
  Divider,
  Grid
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getAuthHeaders } from '../../../utils/authUtils';


const FirstReaderMappingDialog = ({ open, onClose, mapping = null, onSave }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: mapping?.name || '',
    description: mapping?.description || '',
    source_system: 'ris',
    is_active: mapping?.is_active ?? true,
    first_reader_field: mapping?.first_reader_field || ''
  });
  const [validationError, setValidationError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (mapping) {
      setFormData({
        name: mapping.name || '',
        description: mapping.description || '',
        source_system: 'ris',
        is_active: mapping.is_active ?? true,
        first_reader_field: mapping.first_reader_field || ''
      });
    } else {
      setFormData({
        name: '',
        description: '',
        source_system: 'ris',
        is_active: true,
        first_reader_field: ''
      });
    }
    setValidationError('');
  }, [mapping, open]);

  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setValidationError('');

    try {
      if (!formData.name.trim()) {
        setValidationError(t('admin.hl7Mapping.validation.nameRequired'));
        return;
      }
      if (!formData.first_reader_field.trim()) {
        setValidationError(t('admin.hl7Mapping.validation.firstReaderFieldRequired'));
        return;
      }

      const url = mapping 
        ? `${getApiBaseUrl()}/admin/hl7-mappings/${mapping.id}`
        : `${getApiBaseUrl()}/admin/hl7-mappings`;
      const method = mapping ? 'PUT' : 'POST';

      // Für First Reader Mappings: mapping_rules leer lassen und First Reader Felder korrekt setzen
      const firstReaderMappingData = {
        name: formData.name,
        description: formData.description,
        source_system: 'ris',
        is_active: formData.is_active,
        mapping_rules: {}, // Leere mapping_rules für First Reader
        sample_message: '', // Leere sample_message für First Reader
        pacs_config_file: '', // Leer für First Reader
        accession_number_field: '', // Leer für First Reader
        study_instance_uid_field: '', // Leer für First Reader
        first_reader_field: formData.first_reader_field
      };
      
      console.log('Sending First Reader mapping data:', firstReaderMappingData);
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(firstReaderMappingData)
      });
      
      if (response.ok) {
        // Erfolgreich gespeichert - Dialog schließen und Liste aktualisieren
        onClose();
        onSave();
      } else {
        const errorData = await response.json();
        setValidationError(errorData.detail || t('admin.hl7Mapping.saveError'));
      }
    } catch (error) {
      setValidationError(t('admin.hl7Mapping.networkError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setValidationError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mapping ? t('admin.hl7Mapping.editFirstReaderMapping') : t('admin.hl7Mapping.createFirstReaderMapping')}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              name="name"
              label={t('admin.hl7Mapping.name')}
              value={formData.name}
              onChange={handleFormChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              name="description"
              label={t('admin.hl7Mapping.description')}
              value={formData.description}
              onChange={handleFormChange}
              multiline
              rows={2}
              sx={{ mb: 3 }}
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('admin.hl7Mapping.firstReaderField')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('admin.hl7Mapping.firstReaderFieldHelp')}
            </Typography>
            <TextField
              fullWidth
              name="first_reader_field"
              label={t('admin.hl7Mapping.firstReaderField')}
              value={formData.first_reader_field}
              onChange={handleFormChange}
              required
              placeholder="z.B. OBR.32 oder OBX.16"
              sx={{ mb: 3 }}
            />
            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleFormChange}
                />
              }
              label={t('admin.hl7Mapping.active')}
            />
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading} className="button-cancel">
            {t('common.cancel')}
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={isLoading}
            className="button"
          >
            {isLoading ? 'Speichern...' : (mapping ? t('admin.hl7Mapping.update') : t('admin.hl7Mapping.save'))}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FirstReaderMappingDialog; 